import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY, groupBy } from 'rxjs';
import { switchMap, mergeMap, exhaustMap, map, catchError, filter } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { mappedHttpErrorResponseOperator } from '@adnova/jf-ng-components';

import { AnnouncementService, ProblemDTO } from '../../openapi/shell-service-openapi';
import { AnnouncementArchiveEntitiesActions } from './announcement-archive-entities.actions';
import { AnnouncementArchiveEntitiesSelectors } from './announcement-archive-entities.selectors';

/**
 * Effects für die Archiv-Seite.
 *
 * Handhabt das Laden der paginierten Archiv-Liste und der Bilder.
 */
@Injectable()
export class AnnouncementArchiveEntitiesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(AnnouncementService);
  private readonly logger = inject(NGXLogger);

  /**
   * Lädt eine Seite der Archiv-Liste.
   *
   * Verwendet `switchMap` (Lese-Operation, vorherige Anfrage wird abgebrochen bei Seitenwechsel).
   */
  readonly loadPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementArchiveEntitiesActions.loadPage),
      switchMap(({ page, size }) =>
        this.service.readActiveAnnouncements({ number: page, size }).pipe(
          map((response) =>
            AnnouncementArchiveEntitiesActions.loadPageSuccess({
              announcements: response.content,
              totalElements: response.totalElements,
              page,
            }),
          ),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Laden der Archiv-Seite.', mappedError);
              return AnnouncementArchiveEntitiesActions.loadPageFailure({ error: mappedError });
            }),
          )),
        ),
      ),
    ),
  );

  /**
   * Lädt Bilder für alle Announcements nach erfolgreichem Laden einer Seite.
   * Dispatcht `loadImage` für jedes Announcement mit einer `imageId`.
   */
  readonly loadImagesOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementArchiveEntitiesActions.loadPageSuccess),
      switchMap(({ announcements }) => {
        const imageActions = announcements
          .filter((a) => !!a.imageId)
          .map((a) => AnnouncementArchiveEntitiesActions.loadImage({ imageId: a.imageId! }));
        return imageActions.length > 0 ? imageActions : EMPTY;
      }),
    ),
  );

  /**
   * Lädt ein Announcement-Bild und erzeugt eine Blob-URL.
   *
   * Verwendet `groupBy` + `mergeMap` + `exhaustMap` (pro imageId):
   * Verhindert doppeltes Laden desselben Bildes, erlaubt paralleles Laden verschiedener Bilder.
   */
  readonly loadImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementArchiveEntitiesActions.loadImage),
      // INFO: Bereits gecachte Bilder überspringen. imageUrls werden beim Reset bewahrt,
      // damit beim erneuten Öffnen des Archivs keine redundanten API-Calls entstehen.
      concatLatestFrom(() => this.store.select(AnnouncementArchiveEntitiesSelectors.imageUrls)),
      filter(([{ imageId }, imageUrls]) => !imageUrls[imageId]),
      groupBy(([{ imageId }]) => imageId),
      mergeMap(group$ => group$.pipe(
        exhaustMap(([{ imageId }]) =>
          this.service.readAnnouncementImage(imageId).pipe(
            map((blob) =>
              AnnouncementArchiveEntitiesActions.loadImageSuccess({
                imageId,
                blobUrl: URL.createObjectURL(blob),
              }),
            ),
            catchError((error) => of(error).pipe(
              mappedHttpErrorResponseOperator<ProblemDTO>(error),
              map((mappedError) => {
                this.logger.error('Fehler beim Laden des Archiv-Bildes.', mappedError);
                return AnnouncementArchiveEntitiesActions.loadImageFailure({ imageId, error: mappedError });
              }),
            )),
          ),
        ),
      )),
    ),
  );
}
